import { isWireError, type WireError, type WireErrorCode } from "./wire";
import { REQUEST_TIMEOUT_MS } from "@/config/timeouts";

/**
 * The HTTP transport. One place that knows how to reach the gateway, so the
 * API modules stay about their resources.
 *
 * Built against the SOURCE contract — `control/internal/gateway`,
 * `shared/errors` — and deliberately not against the gateway currently
 * deployed on this machine, which is a stale image: it answers a miss with
 * `{"code":"undefined_endpoint"}` where the source emits `NOT_FOUND`. Aligning
 * a client to a running binary that predates its own repository is how a
 * contract quietly forks.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: WireErrorCode | "UNKNOWN",
    message: string,
    readonly details?: Record<string, unknown>,
    readonly traceId?: string,
    readonly retryable = false,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the caller is not authenticated, which the console must not retry blindly. */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === "UNAUTHORIZED";
  }
}

/**
 * A write the CONSOLE refused before sending it (2.741): a check the platform
 * also applies, run first so a request it would refuse never leaves. Nothing
 * was sent, so the outcome is KNOWN: nothing changed. Before 2.741 these were
 * plain Errors, which `writeOutcome` reads as no answer, so the console said
 * "whether it happened is not known" about a request it never made.
 */
export class NotSentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotSentError";
  }
}

/**
 * A write the platform CONFIRMED whose result could not then be read back
 * (2.743). The change is MADE, so the outcome is known: the console says it was
 * saved, and that only the display could not be refreshed. Before 2.743 this
 * was a plain Error, read as no answer: "whether it happened is not known",
 * about a write the platform had answered.
 *
 * `what` names the change as the sentence's subject: "The new profile".
 */
export class SavedNotShownError extends Error {
  constructor(readonly what: string, readonly cause?: unknown) {
    super(`${what} was saved, but the display could not be refreshed`);
    this.name = "SavedNotShownError";
  }
}

const MOCK_STATUS: Partial<Record<WireErrorCode, number>> = {
  INVALID_REQUEST: 400, NOT_FOUND: 404, CONFLICT: 409, INVALID_TRANSITION: 409,
};

/**
 * A mock-mode refusal, shaped as the platform's own answer (2.741, S-691): an
 * `ApiError` with the code the platform answers for the same case, so mock
 * mode shows a refusal as a refusal rather than as "not known".
 */
export function mockRefusal(code: WireErrorCode, message: string): ApiError {
  return new ApiError(MOCK_STATUS[code] ?? 400, code, message);
}

/**
 * Where the gateway is.
 *
 * A build-time value because there is nowhere else to read it from: S-172 —
 * publishing the platform's own addresses — is open, and S-178 found its ruling
 * cannot be implemented as stated. Until that settles, the deployment tells the
 * console where to look.
 */
export function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");
}

/**
 * The bearer token.
 *
 * There is NO login flow yet: the console's sign-in page is not wired to
 * Keycloak, so nothing mints this. It is settable so a developer can paste one
 * from `deploy/dev/tenant-up.sh` and exercise the real gateway, and so the OIDC
 * slice has one place to fill in. A request without it is sent anyway and the
 * gateway answers 401 — which is the honest outcome, and better than the client
 * inventing a reason not to try.
 */
let bearerToken: string | undefined =
  (import.meta.env.VITE_API_TOKEN as string | undefined) || undefined;

export function setBearerToken(token: string | undefined): void {
  bearerToken = token || undefined;
}

export function getBearerToken(): string | undefined {
  return bearerToken;
}

/** Writes carry one so a retry cannot create a second run (IDEMPOTENCY_CONFLICT). */
function idempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

interface RequestOptions {
  /**
   * `PUT` joined this list for FR-87's budget verb (2.431) — the first PUT the
   * console has ever sent. The API uses it where a write REPLACES a whole named
   * resource rather than patching part of one, which is why the budget is a
   * resource of its own and not a field on the organisation.
   */
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Query parameters; undefined values are omitted rather than sent empty. An
   * array is sent as the parameter REPEATED, once per value (2.978's `state`).
   */
  query?: Record<string, string | number | undefined | readonly string[]>;
}

function withQuery(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) for (const v of value) params.append(key, v);
    else if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, ...rest } = options;
  return send<T>(path, rest, body === undefined ? undefined : { text: JSON.stringify(body), contentType: "application/json" });
}

interface RawRequestOptions extends Omit<RequestOptions, "body" | "method"> {
  method?: "POST" | "PUT";
  /** Sent AS-IS: no JSON encoding, no envelope. */
  body: string;
  /** The body's media type, e.g. `application/yaml`. */
  contentType: string;
}

/**
 * A write whose body IS a document rather than a JSON envelope around one —
 * FR-16's schema upload (2.622), where the gateway reads the raw bytes under
 * the media type the client names. Everything else is `request`'s: the bearer
 * token, an `Idempotency-Key`, and the same refusal mapping.
 */
export async function requestRaw<T>(path: string, options: RawRequestOptions): Promise<T> {
  const { body, contentType, ...rest } = options;
  return send<T>(path, { ...rest, method: rest.method ?? "POST" }, { text: body, contentType });
}

/**
 * The platform did not answer within `REQUEST_TIMEOUT_MS` (S-1063). Not an
 * `ApiError`: nothing answered, so a write's outcome is NOT KNOWN and
 * `writeOutcome` says so, and re-reads, rather than calling it failed.
 */
export class RequestTimeoutError extends Error {
  constructor(readonly afterMs: number) {
    super(`The platform did not answer within ${Math.round(afterMs / 1000)} seconds.`);
    this.name = "RequestTimeoutError";
  }
}

/**
 * One exchange, bounded (S-1063): the request AND its body are raced against
 * `REQUEST_TIMEOUT_MS`, so neither a silent connection nor a body that stops
 * mid-read can hold a caller for ever. On expiry the fetch is aborted too. A
 * caller's own signal still cancels it, as before.
 */
async function send<T>(
  path: string,
  options: Omit<RequestOptions, "body">,
  body: { text: string; contentType: string } | undefined,
): Promise<T> {
  const controller = new AbortController();
  const outer = options.signal;
  const forward = () => controller.abort(outer?.reason);
  if (outer?.aborted) forward();
  else outer?.addEventListener("abort", forward, { once: true });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const err = new RequestTimeoutError(REQUEST_TIMEOUT_MS);
      controller.abort(err);
      reject(err);
    }, REQUEST_TIMEOUT_MS);
  });
  try {
    return await Promise.race([exchange<T>(path, options, body, controller.signal), deadline]);
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", forward);
  }
}

async function exchange<T>(
  path: string,
  options: Omit<RequestOptions, "body" | "signal">,
  body: { text: string; contentType: string } | undefined,
  signal: AbortSignal,
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };

  if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
  if (body !== undefined) headers["Content-Type"] = body.contentType;
  // Only writes. A GET carrying one would be meaningless and the header is on
  // the CORS allow-list for the verbs that need it.
  if (method !== "GET") headers["Idempotency-Key"] = idempotencyKey();

  const response = await fetch(`${apiBaseUrl()}${withQuery(path, options.query)}`, {
    method,
    headers,
    body: body?.text,
    signal,
    // The token is a header, not a cookie: sending credentials would ask the
    // gateway for a CORS posture it does not offer.
    credentials: "omit",
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? safeJson(text) : undefined;

  if (!response.ok) throw toApiError(response.status, parsed);
  return parsed as T;
}

/**
 * Fetch a document's BYTES from the gateway — a report or a finding's evidence
 * (IFC-017, IFC-015) — with the same token, origin and refusal handling as
 * `request`.
 *
 * It exists because a plain `<a href download>` cannot do any of that, and the
 * console shipped one: a relative href resolves against the CONSOLE's origin,
 * whose web server answers every unknown path with the application shell, and a
 * navigation carries no bearer token. The user was handed a blank HTML page as
 * their report (2.511).
 *
 * A `text/html` answer is REFUSED rather than returned: the gateway serves these
 * documents as PDF, JSON, CSV or octet-stream and never as a web page, so a page
 * here is a misroute — a wrong base URL, an intermediary's error — and saving
 * it would repeat the defect silently.
 */
export async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const headers: Record<string, string> = { Accept: "*/*" };
  if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

  const response = await fetch(`${apiBaseUrl()}${path}`, {
    method: "GET",
    headers,
    signal,
    credentials: "omit",
  });

  if (!response.ok) {
    const text = await response.text();
    throw toApiError(response.status, text ? safeJson(text) : undefined);
  }
  const type = response.headers.get("Content-Type") ?? "";
  if (/^text\/html\b/i.test(type)) {
    throw new ApiError(response.status, "UNKNOWN",
      "The download returned a web page instead of the file, so it was not saved. " +
      "The console may be pointed at the wrong platform address.");
  }
  return response.blob();
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * Turns a failed response into a typed error.
 *
 * A body that is not the platform's envelope is reported as UNKNOWN rather than
 * guessed at — an intermediary's HTML error page, or a gateway older than this
 * client, must not be read as though it were a machine code the console
 * branches on.
 */
function toApiError(status: number, body: unknown): ApiError {
  if (isWireError(body)) {
    const wire = body as WireError;
    return new ApiError(status, wire.code, wire.message, wire.details, wire.trace_id, wire.retryable);
  }
  const message =
    typeof (body as { message?: unknown })?.message === "string"
      ? (body as { message: string }).message
      : `The platform returned ${status}.`;
  return new ApiError(status, "UNKNOWN", message);
}

/**
 * Who the current token says the caller is.
 *
 * Pushed in by the auth context, the same way the bearer token is, so the API
 * modules can name a user without importing React — and so nothing in `lib/api`
 * has to parse a JWT for itself.
 */
export interface Identity {
  sub: string;
  organisationId?: string;
  name?: string;
  email?: string;
}

let identity: Identity | undefined;

export function setIdentity(next: Identity | undefined): void {
  identity = next;
}

export function getIdentity(): Identity | undefined {
  return identity;
}
